import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import {
    Collapse,
    Paper,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";
import classNames from "classnames";
import { SmallBody, SmallBodyHighlight } from "helpers/TypographyComponents";
import type { ReactNode } from "react";
import { Fragment, useRef, useState } from "react";

import styles from "./style.module.scss";

/**
 * @typedef {object} DoubleEntryTableRows
 * @property {string} label Row label
 * @property {number[]} values Row values
 * @property {object[]} collapseData Rows data inside collapsible row
 */
export type DoubleEntryTableRows = {
    label: string;
    values: number[] | string[];
    collapseRows?: { label: string; values: number[] | string[] }[];
};

/**
 * @typedef DoubleEntryTableColumns
 * @property {string} label Column label
 * @property {string} align Column alignment (Mui prop)
 */
export type DoubleEntryTableColumns = {
    label: string;
    align?: "left" | "center" | "right";
};

interface IDoubleEntryTable {
    /**
     * Table main label, at the top left corner of the table
     */
    tableLabel?: string | ReactNode;
    /**
     * Table columns labels
     */
    columns: DoubleEntryTableColumns[];
    /**
     * Table rows containing labels and values
     */
    rows: DoubleEntryTableRows[];
    /**
     * Table label , at the top left corner of the table inside collapse
     */
    collapsedTableLabel?: string;
    /**
     * Is data loading to render skeleton
     */
    isLoading?: boolean;
}

/**
 * Component that renders a double entry table using MUI with customizable columns, rows, and data.
 */
const DoubleEntryTable = ({
    tableLabel = "",
    columns,
    rows,
    collapsedTableLabel = "",
    isLoading = false,
}: IDoubleEntryTable) => {
    const [collapsedRows, setCollapsedRows] = useState<string[]>([]);

    const tRowCellLabelRef = useRef<HTMLTableHeaderCellElement | null>(null);
    const tRowCellRef = useRef<(HTMLTableCellElement | null)[]>([]);

    const handleCollapseRow = (rowLabel: string) => {
        if (!collapsedRows.includes(rowLabel)) setCollapsedRows((prev) => [...prev, rowLabel]);
        else setCollapsedRows((prev) => prev.filter((item) => item !== rowLabel));
    };

    const cellLabelWidth = `${tRowCellLabelRef?.current?.getBoundingClientRect().width}px`;

    const loadingData = columns.map((_, i) => i);

    return (
        <TableContainer component={Paper} className={styles["double-entry-table"]} elevation={0}>
            <Table stickyHeader size="small" aria-label="double-entry-table">
                {/** TABLEA HEAD */}
                <TableHead className={styles["head"]}>
                    <TableRow>
                        <TableCell className={styles["head-cell"]}>
                            <div className={styles["row-label"]}>
                                <ExpandMoreOutlinedIcon className={styles["svg-hidden"]} />
                                <SmallBodyHighlight color="grey">{tableLabel}</SmallBodyHighlight>
                            </div>
                        </TableCell>
                        {columns.map((col) => (
                            <TableCell key={col.label} align={col.align} className={styles["head-cell"]}>
                                <SmallBodyHighlight color="grey">{col.label}</SmallBodyHighlight>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>

                {/** LOADING TABLE BODY */}
                {isLoading &&
                    loadingData.map((row, i) => {
                        const isLastRow = i === loadingData.length - 1;
                        return (
                            <Fragment key={`loading-row-${i}`}>
                                <TableRow
                                    className={classNames({ [styles["row"]]: true, [styles["row-loading"]]: true })}
                                >
                                    <TableCell
                                        component="th"
                                        scope="row"
                                        className={!isLastRow ? styles["row-cell"] : ""}
                                        key={`loading-cell-${i}`}
                                    >
                                        <Skeleton sx={{ width: "100%" }} />
                                    </TableCell>
                                    {loadingData.map((row, i) => (
                                        <TableCell
                                            component="th"
                                            scope="row"
                                            className={!isLastRow ? styles["row-cell"] : ""}
                                            key={`loading-cell-${i}`}
                                        >
                                            <Skeleton sx={{ width: "100%" }} />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </Fragment>
                        );
                    })}

                {/** TABLE BODY */}
                <TableBody>
                    {!isLoading &&
                        rows.map((row) => {
                            const hasCollapsedData = !!row.collapseRows?.length;
                            const isCollapsed = collapsedRows.includes(row.label);
                            const isTotal = row.label === "Total";
                            return (
                                <Fragment key={row.label}>
                                    <TableRow
                                        className={classNames({
                                            [styles["row"]]: true,
                                            [styles["collapsed"]]: isCollapsed,
                                        })}
                                        onClick={() => hasCollapsedData && handleCollapseRow(row.label)}
                                    >
                                        <TableCell
                                            component="th"
                                            scope="row"
                                            className={classNames({
                                                [styles["row-cell"]]: true,
                                                [styles["row-disabled"]]: !hasCollapsedData,
                                            })}
                                            ref={tRowCellLabelRef}
                                        >
                                            <div className={styles["row-label"]}>
                                                <ExpandMoreOutlinedIcon
                                                    className={classNames({
                                                        [styles["svg-rotate"]]: isCollapsed,
                                                        [styles["svg-hidden"]]: !hasCollapsedData,
                                                    })}
                                                />
                                                {isTotal ? (
                                                    <SmallBodyHighlight style={{ fontSize: "11px" }}>
                                                        {row.label}
                                                    </SmallBodyHighlight>
                                                ) : (
                                                    <SmallBody>{row.label}</SmallBody>
                                                )}
                                            </div>
                                        </TableCell>
                                        {row.values.map((value, index) => (
                                            <TableCell
                                                key={`${row.label}-${index}`}
                                                align={columns[index].align}
                                                className={classNames({
                                                    [styles["row-cell"]]: true,
                                                    [styles["row-disabled"]]: !hasCollapsedData,
                                                })}
                                                ref={(el: HTMLTableCellElement) => {
                                                    if (tRowCellRef.current) tRowCellRef.current[index] = el;
                                                }}
                                            >
                                                {isTotal ? (
                                                    <SmallBodyHighlight style={{ fontSize: "11px" }}>
                                                        {value}
                                                    </SmallBodyHighlight>
                                                ) : (
                                                    <SmallBody>{value}</SmallBody>
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>

                                    {/** COLLAPSIBLE ROW DETAILS */}
                                    <TableRow>
                                        <TableCell
                                            className={styles["row-details-wrapper"]}
                                            colSpan={columns.length + 1}
                                        >
                                            <Collapse in={isCollapsed} timeout={"auto"}>
                                                <Table
                                                    size="small"
                                                    aria-label="double-entry-table-details"
                                                    className={styles["details-table"]}
                                                >
                                                    <TableBody className={styles["details-head"]}>
                                                        {/** TableBody here because head is too heavy */}
                                                        <TableRow>
                                                            <TableCell
                                                                className={styles["details-head-cell"]}
                                                                style={{ width: cellLabelWidth }}
                                                            >
                                                                <div className={styles["row-label"]}>
                                                                    <ExpandMoreOutlinedIcon
                                                                        className={styles["svg-hidden"]}
                                                                    />
                                                                    <SmallBodyHighlight>
                                                                        {collapsedTableLabel}
                                                                    </SmallBodyHighlight>
                                                                </div>
                                                            </TableCell>
                                                            {columns.map((col, index) => {
                                                                const cellWidth = `${tRowCellRef.current[
                                                                    index
                                                                ]?.getBoundingClientRect().width}px`;
                                                                return (
                                                                    <TableCell
                                                                        key={col.label}
                                                                        align={col.align}
                                                                        className={styles["details-head-cell"]}
                                                                        style={{ width: cellWidth }}
                                                                    >
                                                                        <SmallBodyHighlight>
                                                                            {col.label}
                                                                        </SmallBodyHighlight>
                                                                    </TableCell>
                                                                );
                                                            })}
                                                        </TableRow>
                                                    </TableBody>

                                                    <TableBody>
                                                        {row.collapseRows?.map((collapseRow) => (
                                                            <TableRow
                                                                key={collapseRow.label}
                                                                className={styles["details-row"]}
                                                            >
                                                                <TableCell
                                                                    component="th"
                                                                    scope="row"
                                                                    className={styles["details-cell"]}
                                                                >
                                                                    <div className={styles["row-label"]}>
                                                                        <ExpandMoreOutlinedIcon
                                                                            className={styles["svg-hidden"]}
                                                                        />
                                                                        <SmallBody>{collapseRow.label}</SmallBody>
                                                                    </div>
                                                                </TableCell>
                                                                {collapseRow.values.map((value, index) => (
                                                                    <TableCell
                                                                        key={`${collapseRow.label}-${index}`}
                                                                        align={columns[index].align}
                                                                        className={styles["details-cell"]}
                                                                    >
                                                                        <SmallBody>{value}</SmallBody>
                                                                    </TableCell>
                                                                ))}
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </Fragment>
                            );
                        })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default DoubleEntryTable;
